import { TransferTemplateForm } from '../types';
import {
  AchTransferTemplate,
  AchTransferTemplateResponse,
  BookTransferTemplate,
  BookTransferTemplateResponse,
  CreateTransferTemplateRequest,
  TransferTemplateType,
  UpdateTransferTemplateRequest,
  WireTransferTemplate,
  WireTransferTemplateResponse,
} from '~/repositories/Transfer/TransferTemplateRepository';

export interface ValidationError {
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export const validateTransfers = (
  templateType: TransferTemplateType,
  formData: TransferTemplateForm
): ValidationResult => {
  const errors: ValidationError[] = [];

  const validateTransferArray = (
    type: TransferTemplateType,
    transfers: AchTransferTemplateResponse[] | BookTransferTemplateResponse[] | WireTransferTemplateResponse[],
    typeName: string
  ): void => {
    if (templateType === type) {
      if (!transfers || transfers.length <= 0) {
        errors.push({ message: `Please add at least one ${typeName} transfer` });
        return;
      }

      transfers.forEach(
        (
          transfer: AchTransferTemplateResponse | BookTransferTemplateResponse | WireTransferTemplateResponse,
          index: number
        ) => {
          const missingFields = [];

          if (type === TransferTemplateType.ACH || type === TransferTemplateType.Wire) {
            if (!('counterpartyId' in transfer)) {
              missingFields.push('Counterparty');
            }

            if (!('bankAccountId' in transfer)) {
              missingFields.push('Bank Account');
            }

            if (type === TransferTemplateType.ACH && !('type' in transfer)) {
              missingFields.push('Type');
            }

            if (type === TransferTemplateType.ACH && !('entryClassCode' in transfer)) {
              missingFields.push('Entry Class Code');
            }
          }

          if (type === TransferTemplateType.Book) {
            if (!('senderBankAccountId' in transfer)) {
              missingFields.push('Sender Bank Account');
            }

            if (!('receiverBankAccountId' in transfer)) {
              missingFields.push('Receiver Bank Account');
            }
          }

          if (missingFields.length > 0) {
            errors.push({
              message: `Transfer #${index + 1} is missing required fields: ${missingFields.join(', ')}`,
            });
          }
        }
      );
    }
  };

  validateTransferArray(TransferTemplateType.ACH, formData?.achTransfers ?? [], 'ACH');
  validateTransferArray(TransferTemplateType.Wire, formData?.wireTransfers ?? [], 'Wire');
  validateTransferArray(TransferTemplateType.Book, formData?.bookTransfers ?? [], 'Book');

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const buildAchTransfers = (transfers: AchTransferTemplateResponse[]): AchTransferTemplate[] | undefined => {
  if (!transfers || transfers.length === 0) return undefined;

  return transfers.map((transfer) => ({
    transferTemplateRowId: transfer.transferTemplateRowId,
    amount: transfer.amount,
    accountNumberId: transfer.accountNumberId,
    allowOverdraft: transfer.allowOverdraft,
    bankAccountId: transfer.bankAccountId,
    counterpartyId: transfer.counterpartyId,
    currencyCode: transfer.currencyCode,
    description: transfer.description,
    companyDiscretionaryData: transfer.companyDiscretionaryData,
    companyEntryDescription: transfer.companyEntryDescription,
    companyName: transfer.companyName,
    entryClassCode: transfer.entryClassCode,
    paymentRelatedInfo: transfer.paymentRelatedInfo,
    receiverName: transfer.receiverName,
    receiverId: transfer.receiverId,
    type: transfer.type,
  }));
};

const buildWireTransfers = (transfers: WireTransferTemplateResponse[]): WireTransferTemplate[] | undefined => {
  if (!transfers || transfers.length === 0) return undefined;

  return transfers.map((transfer) => ({
    transferTemplateRowId: transfer.transferTemplateRowId,
    amount: transfer.amount,
    accountNumberId: transfer.accountNumberId,
    allowOverdraft: transfer.allowOverdraft,
    bankAccountId: transfer.bankAccountId,
    counterpartyId: transfer.counterpartyId,
    currencyCode: transfer.currencyCode,
    description: transfer.description,
  }));
};

const buildBookTransfers = (transfers: BookTransferTemplateResponse[]): BookTransferTemplate[] | undefined => {
  if (!transfers || transfers.length === 0) return undefined;

  return transfers.map((transfer) => ({
    transferTemplateRowId: transfer.transferTemplateRowId,
    amount: transfer.amount,
    allowOverdraft: transfer.allowOverdraft,
    currencyCode: transfer.currencyCode,
    description: transfer.description,
    receiverAccountNumberId: transfer.receiverAccountNumberId,
    receiverBankAccountId: transfer.receiverBankAccountId,
    senderAccountNumberId: transfer.senderAccountNumberId,
    senderBankAccountId: transfer.senderBankAccountId,
  }));
};

const buildAchDefaultParameters = (
  params: TransferTemplateForm['achDefaultParameters']
): AchTransferTemplate | undefined => {
  if (!params) return undefined;

  const cleanParams: AchTransferTemplate = {};

  if (params.amount) cleanParams.amount = params.amount;
  if (params.accountNumberId) cleanParams.accountNumberId = params.accountNumberId;
  if (params.allowOverdraft !== undefined) cleanParams.allowOverdraft = params.allowOverdraft;
  if (params.bankAccountId) cleanParams.bankAccountId = params.bankAccountId;
  if (params.counterpartyId) cleanParams.counterpartyId = params.counterpartyId;
  if (params.currencyCode) cleanParams.currencyCode = params.currencyCode;
  if (params.description) cleanParams.description = params.description;
  if (params.companyDiscretionaryData) cleanParams.companyDiscretionaryData = params.companyDiscretionaryData;
  if (params.companyEntryDescription) cleanParams.companyEntryDescription = params.companyEntryDescription;
  if (params.companyName) cleanParams.companyName = params.companyName;
  if (params.entryClassCode) cleanParams.entryClassCode = params.entryClassCode;
  if (params.paymentRelatedInfo) cleanParams.paymentRelatedInfo = params.paymentRelatedInfo;
  if (params.receiverName) cleanParams.receiverName = params.receiverName;
  if (params.receiverId) cleanParams.receiverId = params.receiverId;
  if (params.type) cleanParams.type = params.type;

  return Object.keys(cleanParams).length > 0 ? cleanParams : undefined;
};

const buildWireDefaultParameters = (
  params: TransferTemplateForm['wireDefaultParameters']
): WireTransferTemplate | undefined => {
  if (!params) return undefined;

  const cleanParams: WireTransferTemplate = {};

  if (params.amount) cleanParams.amount = params.amount;
  if (params.accountNumberId) cleanParams.accountNumberId = params.accountNumberId;
  if (params.allowOverdraft !== undefined) cleanParams.allowOverdraft = params.allowOverdraft;
  if (params.bankAccountId) cleanParams.bankAccountId = params.bankAccountId;
  if (params.counterpartyId) cleanParams.counterpartyId = params.counterpartyId;
  if (params.currencyCode) cleanParams.currencyCode = params.currencyCode;
  if (params.description) cleanParams.description = params.description;

  return Object.keys(cleanParams).length > 0 ? cleanParams : undefined;
};

const buildBookDefaultParameters = (
  params: TransferTemplateForm['bookDefaultParameters']
): BookTransferTemplate | undefined => {
  if (!params) return undefined;

  const cleanParams: BookTransferTemplate = {};

  if (params.amount) cleanParams.amount = params.amount;
  if (params.allowOverdraft !== undefined) cleanParams.allowOverdraft = params.allowOverdraft;
  if (params.currencyCode) cleanParams.currencyCode = params.currencyCode;
  if (params.description) cleanParams.description = params.description;
  if (params.receiverAccountNumberId) cleanParams.receiverAccountNumberId = params.receiverAccountNumberId;
  if (params.receiverBankAccountId) cleanParams.receiverBankAccountId = params.receiverBankAccountId;
  if (params.senderAccountNumberId) cleanParams.senderAccountNumberId = params.senderAccountNumberId;
  if (params.senderBankAccountId) cleanParams.senderBankAccountId = params.senderBankAccountId;

  return Object.keys(cleanParams).length > 0 ? cleanParams : undefined;
};

export const buildCreateTransferTemplateRequest = (formData: TransferTemplateForm): CreateTransferTemplateRequest => {
  const request: CreateTransferTemplateRequest = {
    description: formData.description,
    templateType: formData.templateType,
  };

  if (formData.visibleColumns) {
    request.visibleColumns = formData.visibleColumns;
  }

  switch (formData.templateType) {
    case TransferTemplateType.ACH:
      request.achDefaultParameters = buildAchDefaultParameters(formData.achDefaultParameters);
      request.achTransfers = buildAchTransfers(formData.achTransfers ?? []);
      break;

    case TransferTemplateType.Wire:
      request.wireDefaultParameters = buildWireDefaultParameters(formData.wireDefaultParameters);
      request.wireTransfers = buildWireTransfers(formData.wireTransfers ?? []);
      break;

    case TransferTemplateType.Book:
      request.bookDefaultParameters = buildBookDefaultParameters(formData.bookDefaultParameters);
      request.bookTransfers = buildBookTransfers(formData.bookTransfers ?? []);
      break;
  }

  return request;
};

export const buildUpdateTransferTemplateRequest = (
  formData: TransferTemplateForm,
  transferTemplateId: string
): UpdateTransferTemplateRequest => {
  const request: UpdateTransferTemplateRequest = {
    transferTemplateId,
    description: formData.description,
  };

  if (formData.visibleColumns) {
    request.visibleColumns = formData.visibleColumns;
  }

  switch (formData.templateType) {
    case TransferTemplateType.ACH:
      request.achDefaultParameters = buildAchDefaultParameters(formData.achDefaultParameters);
      request.achTransfers = buildAchTransfers(formData.achTransfers ?? []);
      break;

    case TransferTemplateType.Wire:
      request.wireDefaultParameters = buildWireDefaultParameters(formData.wireDefaultParameters);
      request.wireTransfers = buildWireTransfers(formData.wireTransfers ?? []);
      break;

    case TransferTemplateType.Book:
      request.bookDefaultParameters = buildBookDefaultParameters(formData.bookDefaultParameters);
      request.bookTransfers = buildBookTransfers(formData.bookTransfers ?? []);
      break;
  }

  return request;
};

export const buildAchTransferUpdateRequest = (
  transfers: Partial<AchTransferTemplateResponse>[]
): AchTransferTemplate[] => {
  return transfers.map((transfer) => ({
    transferTemplateRowId: transfer.transferTemplateRowId,
    amount: transfer.amount,
    counterpartyId: transfer.counterpartyId,
    type: transfer.type,
    bankAccountId: transfer.bankAccountId,
    description: transfer.description,
    entryClassCode: transfer.entryClassCode,
    allowOverdraft: transfer.allowOverdraft,
    companyName: transfer.companyName,
    companyDiscretionaryData: transfer.companyDiscretionaryData,
    companyEntryDescription: transfer.companyEntryDescription,
    paymentRelatedInfo: transfer.paymentRelatedInfo,
    receiverName: transfer.receiverName,
    receiverId: transfer.receiverId,
  }));
};

export const buildWireTransferUpdateRequest = (
  transfers: Partial<WireTransferTemplateResponse>[]
): WireTransferTemplate[] => {
  return transfers.map((transfer) => ({
    transferTemplateRowId: transfer.transferTemplateRowId,
    amount: transfer.amount,
    counterpartyId: transfer.counterpartyId,
    bankAccountId: transfer.bankAccountId,
    description: transfer.description,
    allowOverdraft: transfer.allowOverdraft,
  }));
};

export const buildBookTransferUpdateRequest = (
  transfers: Partial<BookTransferTemplateResponse>[]
): BookTransferTemplate[] => {
  return transfers.map((transfer) => ({
    transferTemplateRowId: transfer.transferTemplateRowId,
    amount: transfer.amount,
    senderBankAccountId: transfer.senderBankAccountId,
    receiverBankAccountId: transfer.receiverBankAccountId,
    description: transfer.description,
    allowOverdraft: transfer.allowOverdraft,
  }));
};
